import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import M_PER_NEW_02 from './M_PER_NEW_02';
import { useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { BottomSheetActions } from '../../../../Reducer/BottomSheetReducer';
import { BaseInfoActions } from '../../../../Reducer/BaseInfoReducer';
import useFcmHandler from '../../../../Hooks/useFcmHandler';
import { CommonActions } from '../../../../Reducer/CommonReducer';
import { NavBarInfoActions } from '../../../../Reducer/NavBarInfoReducer';
import {
  useDelete1,
  useSave1,
  useSave2,
  useSave6,
  useSearch1,
} from './M_PER_NEW_02_Query';
import { useDelete } from '../../../../Hooks/useDelete';
import { PermitActions } from '../../../../Reducer/PermitReducer';
import { useSave } from '../../../../Hooks/useSave';
import { M_PER_NEW_02_Schema } from './M_PER_NEW_02_Schema';
import useFileUpload from '../../../../Hooks/useFileUpload';
import { useAppNavigate } from '../../../../Hooks/back/useAppNavigate';

export default function M_PER_NEW_02F() {
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const { baseInfo } = useSelector((state) => state.permit);

  const dispatch = useDispatch();
  const { id: dailyCdFromRoute } = useParams();
  const location = useLocation();
  const { navigate } = useAppNavigate();
  const signatureCanvas = useRef();

  const isWitnessMode = location.state?.mode === 'witness';
  const sendFcm = useFcmHandler();

  const searchDailyCd = String(
    dailyCdFromRoute || location.state?.dailyCd || ''
  );

  const getFcmDailyCd = () =>
    String(
      baseInfo.currentDailyCd ??
        baseInfo.dailies?.[0]?.dailyCd ??
        dailyCdFromRoute ??
        ''
    );

  const [activeIndex, setActiveIndex] = useState(0);
  const [isBaseChecked, setIsBaseChecked] = useState(false);
  const [isChecklistChecked, setIsChecklistChecked] = useState(false);
  const [completedSections, setCompletedSections] = useState([]);
  const [isComplete, setIsComplete] = useState(false);

  const [witnessChecksByTab, setWitnessChecksByTab] = useState({});
  const [checkRemarksByTab, setCheckRemarksByTab] = useState({});
  // 탭(workItemId)별 서명 파일/URL 맵
  const [witnessSignFiles, setWitnessSignFiles] = useState({});
  const [witnessSignUrls, setWitnessSignUrls] = useState({});
  const witnessSignFilesRef = useRef({});
  const witnessSignUrlsRef = useRef({});
  const workEndSignFlowRef = useRef({ active: false });
  const witnessSaveRef = useRef(() => {});

  const [unsignedForWorkEnd, setUnsignedForWorkEnd] = useState([]);
  const [signFlowTabId, setSignFlowTabId] = useState('');
  const [signFlowTrigger, setSignFlowTrigger] = useState(0);
  const [isWorkEndSignFlow, setIsWorkEndSignFlow] = useState(false);

  const handleWitnessCheck = (tabId, section, idx, value) => {
    setWitnessChecksByTab((prev) => ({
      ...prev,
      [tabId]: {
        ...(prev[tabId] ?? {}),
        [section]: {
          ...(prev[tabId]?.[section] ?? {}),
          [idx]: value,
        },
      },
    }));
  };

  const handleCheckRemarkChange = (tabId, value) => {
    setCheckRemarksByTab((prev) => ({ ...prev, [tabId]: value }));
  };

  const { createPreviewItem, SaveLogicFile } = useFileUpload({
    oldFilePathField: 'oldPath',
    newFileNameField: 'saveName',
    getTargetPath: () =>
      `Business/${userInfo.custCd}/SAF_BASIC_02/${userInfo.decUserId}`,
  });

  const getUnsignedWorkTypes = useCallback(() => {
    const details = baseInfo.dailies?.[0]?.details || [];

    return details
      .filter((detail) => {
        const workItemId = String(detail.workItemId);
        return !(
          witnessSignFilesRef.current[workItemId] ||
          witnessSignUrlsRef.current[workItemId] ||
          detail.witnessSignPath
        );
      })
      .map((detail) => ({
        workItemId: String(detail.workItemId),
        workTypeNm: detail.workTypeNm,
      }));
  }, [baseInfo.dailies]);

  const triggerSignForTab = (workItemId) => {
    setSignFlowTabId(String(workItemId));
    setSignFlowTrigger((prev) => prev + 1);
  };

  const continueWorkEndSignFlow = useCallback(() => {
    const remaining = getUnsignedWorkTypes();

    if (remaining.length > 0) {
      triggerSignForTab(remaining[0].workItemId);
      return;
    }

    workEndSignFlowRef.current.active = false;
    setIsWorkEndSignFlow(false);
    setSignFlowTabId('');
    witnessSaveRef.current({ workEndGubun: 'WORK_END' });
  }, [getUnsignedWorkTypes]);

  useEffect(() => {
    dispatch(PermitActions.resetBaseInfo());
    setWitnessChecksByTab({});
    setCheckRemarksByTab({});
    setWitnessSignFiles({});
    setWitnessSignUrls({});
    witnessSignFilesRef.current = {};
    witnessSignUrlsRef.current = {};
    workEndSignFlowRef.current = { active: false };
    setUnsignedForWorkEnd([]);
    setSignFlowTabId('');
    setSignFlowTrigger(0);
    setIsWorkEndSignFlow(false);
    setIsComplete(false);

    dispatch(NavBarInfoActions.setType('titleBar'));
    dispatch(NavBarInfoActions.setTitleName('작업 허가서'));
    dispatch(CommonActions.setActiveStep(0));

    if (!searchDailyCd) return;

    Search1({ dailyCd: searchDailyCd });
  }, [dispatch, searchDailyCd]);

  // 작업허가서 FCM (gubun : 2 : 안전관리자가 서명 후 알람, 1 : 결재승인자이 서명 후 알람)
  const handleSignRequest = async (gubun) => {
    dispatch(BaseInfoActions.setFcmReceptionPayload(''));

    if (gubun === '1') {
      await sendFcm({
        targetUserId: baseInfo.mst.smId,
        requestUserId: userInfo.userid,
        body: '작업 허가서 요청입니다.',
        status: '요청',
        codeGroup: 'AL_002_001',
        title: '작업허가서',
        route: '/M_Permit',
        responseData: getFcmDailyCd(),
      });
      return;
    }

    if (gubun === '0') {
      await sendFcm({
        targetUserId: baseInfo.mst.reqId,
        requestUserId: userInfo.userid,
        body: '작업허가서 반려 안내',
        status: '반려',
        codeGroup: 'AL_002_002',
        title: '작업허가서',
        route: '/M_Permit',
        responseData: getFcmDailyCd(),
      });
      return;
    }

    if (gubun === '2') {
      await sendFcm({
        targetUserId: baseInfo.mst?.reqId,
        requestUserId: userInfo.userid,
        body: '작업허가서 승인이 완료되었습니다.',
        status: '최종승인',
        codeGroup: 'AL_002_003',
        title: '작업허가서',
        route: '/M_Permit',
        responseData: getFcmDailyCd(),
      });

      await sendFcm({
        targetUserId: baseInfo.mst.witnessId,
        requestUserId: userInfo.userid,
        body: '귀하가 입회자로 지정된 작업이 최종 승인되었습니다.',
        status: '최종승인',
        codeGroup: 'AL_002_003',
        title: '작업허가서(입회)',
        route: '/M_Permit',
        responseData: getFcmDailyCd(),
      });

      return;
    }
  };
  const { run: Search1 } = useSearch1({
    onSuccess: (response) => {
      const resData = Array.isArray(response) ? response[0] : response;
      if (!resData?.dailies?.length) return;

      const daily =
        resData.dailies.find(
          (item) =>
            searchDailyCd && String(item.dailyCd) === String(searchDailyCd)
        ) || resData.dailies[0];
      const details = daily.details || [];
      const currentDailyCd = String(daily.dailyCd ?? '');
      const currentWorkDate = daily.workDate?.split(' ')[0] || '';
      const filteredCenterSigns = (resData.centerSigns || []).filter(
        (sign) => String(sign.dailyCd) === currentDailyCd
      );

      const detailMap = {};
      const risks = [];
      const equips = [];
      const preps = [];

      details.forEach((detail) => {
        const wId = String(detail.workItemId);
        detailMap[wId] = detail;

        detail.riskItems?.forEach((r) => {
          if (r.itemCheck === '0') risks.push({ ...r, workItemId: wId });
        });
        detail.equipItems?.forEach((e) => {
          if (e.itemCheck === '0') equips.push({ ...e, workItemId: wId });
        });
        detail.prepItems?.forEach((p) => {
          if (p.itemCheck === '0') preps.push({ ...p, workItemId: wId });
        });
      });

      dispatch(
        PermitActions.setAllInfo({
          baseInfo: {
            ...resData,
            centerSigns: filteredCenterSigns,
            dailies: [daily],
            pmCd: resData.mst?.pmCd ?? resData.pmCd ?? '',
            currentDailyCd,
            currentWorkDate,
            detailMap,
            workStartDt: currentWorkDate,
          },
          codeInfo: { risks, equips, preparations: preps },
        })
      );

      // 서버 witnessCheck / checkRemark / witnessSignPath 값으로 로컬 상태 초기화
      const initialChecks = {};
      const initialRemarks = {};
      const initialSignUrls = {};
      details.forEach((detail) => {
        const workItemId = String(detail.workItemId);
        // itemCheck === '0' 인 항목만 AccordionSection에 표시되므로 같은 기준으로 필터
        const filteredPreps = (detail.prepItems || []).filter(
          (p) => p.itemCheck === '0'
        );
        const prepsChecks = {};
        filteredPreps.forEach((prep, idx) => {
          if (prep.witnessCheck === '0') {
            prepsChecks[idx] = true;
          }
        });
        if (Object.keys(prepsChecks).length > 0) {
          initialChecks[workItemId] = { preps: prepsChecks };
        }
        if (detail.checkRemark) {
          initialRemarks[workItemId] = detail.checkRemark;
        }
        if (detail.witnessSignPath) {
          initialSignUrls[workItemId] = detail.witnessSignPath;
        }
      });
      setWitnessChecksByTab(initialChecks);
      setCheckRemarksByTab(initialRemarks);
      setWitnessSignUrls(initialSignUrls);
      witnessSignUrlsRef.current = initialSignUrls;
    },
  });

  const { mutateAsync: save1Async } = useSave1();

  const { run: Save1 } = useSave({
    menuId: 'M_PER_NEW_02',
    schema: M_PER_NEW_02_Schema,

    saveData: (params) => {
      const rawWorkDate =
        params.workDate ??
        baseInfo.currentWorkDate ??
        baseInfo.dailies?.[0]?.workDate ??
        '';

      return {
        ...params,
        baseInfo,
        pmCd: baseInfo.mst?.pmCd ?? baseInfo.pmCd ?? '',
        dailyCd: String(
          params.dailyCd ??
            baseInfo.currentDailyCd ??
            baseInfo.dailies?.[0]?.dailyCd ??
            ''
        ),
        workDate: rawWorkDate
          ? dayjs(rawWorkDate).format('YYYY-MM-DD')
          : '',
      };
    },

    fileSave: async (requestRows, params) => {
      const { gubun, signFile } = params;
      if (gubun !== '0' && signFile) {
        const fileResult = await createPreviewItem(signFile);
        const finalPath = await SaveLogicFile([fileResult]);
        console.log(finalPath);

        if (finalPath) {
          return {
            depSignPath: gubun === '1' ? finalPath : '',
            smSignPath: gubun === '2' ? finalPath : '',
          };
        }
      }
      return {};
    },

    requestSave: async (requestRows) => {
      return await save1Async(requestRows[0]);
    },

    onSuccess: async ({ saveDetailData, requestRows }) => {
      const context = requestRows[0];
      const { gubun } = context;
      const isLastSign = saveDetailData?.[0]?.isLastSign;

      if (gubun === '0') await handleSignRequest('0');
      else if (gubun === '1' && isLastSign === 'Y') {
        await handleSignRequest('1');
        setIsComplete(true);
      } else if (gubun === '2') {
        await handleSignRequest('2');
        setIsComplete(true);
      }

      Search1({ dailyCd: searchDailyCd });

      dispatch(
        showSnackbar({
          message:
            gubun === '0'
              ? '반려 처리가 완료되었습니다.'
              : '승인 처리가 완료되었습니다.',
          type: 'success',
        })
      );
    },
  });

  const { runAsync: Delete1Async } = useDelete1();

  const { run: Delete1 } = useDelete({
    menuId: 'M_PER_NEW_02',
    requestDelete: () => {
      const rawWorkDate =
        baseInfo.currentWorkDate ?? baseInfo.dailies?.[0]?.workDate ?? '';

      return Delete1Async({
        pmCd: baseInfo.mst?.pmCd ?? baseInfo.pmCd ?? '',
        baseInfo,
        dailyCd: String(
          baseInfo.currentDailyCd ?? baseInfo.dailies?.[0]?.dailyCd ?? ''
        ),
        workDate: rawWorkDate
          ? dayjs(rawWorkDate).format('YYYY-MM-DD')
          : '',
      });
    },
    onSuccess: () => {
      dispatch(
        showSnackbar({
          message: '작업허가서가 삭제되었습니다.',
          type: 'success',
        })
      );

      navigate('/M_Permit');
    },
  });

  const { mutateAsync: save2Async } = useSave2();

  const extendedDtlCds = useMemo(() => {
    const details = baseInfo.dailies?.[0]?.details || [];
    return details.filter((d) => d.isExtended === 'Y').map((d) => d.dtlCd);
  }, [baseInfo]);

  const { mutateAsync: save6Async } = useSave6();

  const { run: Save6 } = useSave({
    menuId: 'M_PER_NEW_02',
    schema: null,

    saveData: (params) => ({ ...params }),

    fileSave: async (requestRows, params) => {
      const { gubun, signFile } = params;
      if (gubun === '2' && signFile) {
        const fileResult = await createPreviewItem(signFile);
        const finalPath = await SaveLogicFile([fileResult]);
        if (finalPath) return { smSignPath: finalPath };
      }
      return { smSignPath: '' };
    },

    requestSave: async (requestRows) => {
      return await save6Async(requestRows[0]);
    },

    showSuccess: false,

    onSuccess: async ({ requestRows }) => {
      const gubun = requestRows[0]?.gubun;

      dispatch(
        showSnackbar({
          message:
            gubun === '2'
              ? '연장 승인이 완료되었습니다.'
              : '연장 반려가 완료되었습니다.',
          type: 'success',
        })
      );

      await sendFcm({
        targetUserId: baseInfo.mst?.reqId,
        requestUserId: userInfo.userid,
        title: '작업허가서(연장)',
        body:
          gubun === '2'
            ? '연장 신청이 승인되었습니다.'
            : '연장 신청이 반려되었습니다.',
        status: gubun === '2' ? '연장승인' : '연장반려',
        codeGroup: gubun === '2' ? 'AL_002_003' : 'AL_002_002',
        route: '/M_Permit',
        responseData: getFcmDailyCd(),
      });

      Search1({ dailyCd: searchDailyCd });
    },
  });

  const buildDetailRows = (
    signPathMap,
    witnessSignDate,
    witnessYn,
    onlyTouched = false,
    forcedTabIds = []
  ) => {
    const details = baseInfo.dailies?.[0]?.details || [];

    const targetDetails = onlyTouched
      ? details.filter((detail) => {
          const workItemId = String(detail.workItemId);
          return (
            checkRemarksByTab[workItemId] !== undefined ||
            witnessChecksByTab[workItemId] !== undefined ||
            forcedTabIds.includes(workItemId)
          );
        })
      : details;

    return targetDetails.map((detail) => {
      const workItemId = String(detail.workItemId);
      const filteredPreps = (detail.prepItems || []).filter(
        (p) => p.itemCheck === '0'
      );
      const tabChecks = witnessChecksByTab[workItemId]?.preps ?? {};
      return {
        dtlCd: detail.dtlCd,
        witnessSignPath:
          signPathMap[workItemId] || detail.witnessSignPath || '',
        witnessSignDate,
        witnessYn,
        checkRemark: checkRemarksByTab[workItemId] ?? '',
        prepRows: filteredPreps.map((prep, idx) => ({
          preparationCode: prep.preparationCode,
          witnessCheck: tabChecks[idx] ? '0' : '1',
        })),
      };
    });
  };

  const { run: WitnessSave } = useSave({
    menuId: 'M_PER_NEW_02',
    schema: null,

    saveData: (params) => ({ ...params }),

    requestSave: async (requestRows) => {
      const context = requestRows[0];
      const { workEndGubun, overrideSignFiles } = context;
      const witnessSignDate =
        baseInfo.mst?.totalStartDate || dayjs().format('YYYY-MM-DD HH:mm:ss');
      const witnessYn = workEndGubun === 'WORK_END' ? 'Y' : 'N';

      const sourceFiles = overrideSignFiles ?? witnessSignFiles;
      const signPathMap = {};
      for (const [tabId, file] of Object.entries(sourceFiles)) {
        if (file) {
          const fileResult = await createPreviewItem(file);
          const finalPath = await SaveLogicFile([fileResult]);
          if (finalPath) signPathMap[tabId] = finalPath;
        }
      }

      const detailRows = buildDetailRows(
        signPathMap,
        witnessSignDate,
        witnessYn,
        workEndGubun === 'TEMP_SAVE',
        Object.keys(signPathMap)
      );

      return await save2Async({
        userInfo,
        workEndGubun,
        dtlCd: detailRows[0]?.dtlCd || '',
        witnessSignPath: detailRows[0]?.witnessSignPath || '',
        witnessSignDate,
        witnessYn,
        prepRows: detailRows[0]?.prepRows || [],
        detailRows,
      });
    },

    showSuccess: false,

    onSuccess: async ({ requestRows }) => {
      const workEndGubun = requestRows[0]?.workEndGubun ?? 'TEMP_SAVE';

      if (workEndGubun === 'WORK_END') {
        dispatch(
          showSnackbar({
            message: '작업종료 처리가 완료되었습니다.',
            type: 'success',
          })
        );
        dispatch(CommonActions.setActiveStep(0));
        navigate('/M_Permit');
      } else {
        if (workEndSignFlowRef.current.active) {
          continueWorkEndSignFlow();
          return;
        }

        dispatch(
          showSnackbar({ message: '임시저장 되었습니다.', type: 'success' })
        );
        Search1({ dailyCd: searchDailyCd });
      }
    },
  });

  witnessSaveRef.current = WitnessSave;

  const WitnessTempSave = () => WitnessSave({ workEndGubun: 'TEMP_SAVE' });

  // tabId(workItemId)별로 서명 파일/URL 저장 + 서명 즉시 임시저장
  const WitnessSave1 = ({ signFile, signUrl, tabId } = {}) => {
    if (!tabId) return;

    const updatedSignFiles = {
      ...witnessSignFilesRef.current,
      [tabId]: signFile ?? null,
    };
    const updatedSignUrls = {
      ...witnessSignUrlsRef.current,
      [tabId]: signUrl ?? '',
    };

    witnessSignFilesRef.current = updatedSignFiles;
    witnessSignUrlsRef.current = updatedSignUrls;
    setWitnessSignFiles(updatedSignFiles);
    setWitnessSignUrls(updatedSignUrls);

    WitnessSave({
      workEndGubun: 'TEMP_SAVE',
      overrideSignFiles: updatedSignFiles,
    });
  };

  const startWorkEndSignFlow = () => {
    const unsigned =
      unsignedForWorkEnd.length > 0
        ? unsignedForWorkEnd
        : getUnsignedWorkTypes();

    if (unsigned.length === 0) {
      WitnessSave({ workEndGubun: 'WORK_END' });
      return;
    }

    workEndSignFlowRef.current.active = true;
    setIsWorkEndSignFlow(true);
    dispatch(BottomSheetActions.CloseBottomSheet());
    triggerSignForTab(unsigned[0].workItemId);
  };

  const WitnessWorkEnd = () => {
    const unsigned = getUnsignedWorkTypes();

    if (unsigned.length > 0) {
      setUnsignedForWorkEnd(unsigned);
      dispatch(
        showSnackbar({
          message: '작업종료 전 모든 작업종류에 입회 서명이 필요합니다.',
          type: 'warning',
        })
      );
      dispatch(
        BottomSheetActions.OpenBottomSheet({ type: 'witnessWorkEndGuide' })
      );
      return;
    }

    WitnessSave({ workEndGubun: 'WORK_END' });
  };

  return (
    <div className="wrap">
      <M_PER_NEW_02
        activeIndex={activeIndex}
        setActiveIndex={setActiveIndex}
        isBaseChecked={isBaseChecked}
        setIsBaseChecked={setIsBaseChecked}
        isChecklistChecked={isChecklistChecked}
        setIsChecklistChecked={setIsChecklistChecked}
        completedSections={completedSections}
        setCompletedSections={setCompletedSections}
        isComplete={isComplete}
        setIsComplete={setIsComplete}
        isWitnessMode={isWitnessMode}
        witnessChecksByTab={witnessChecksByTab}
        onWitnessCheck={handleWitnessCheck}
        checkRemarksByTab={checkRemarksByTab}
        onCheckRemarkChange={handleCheckRemarkChange}
        signatureCanvas={signatureCanvas}
        Save1={Save1}
        Delete1={Delete1}
        Save6={Save6}
        extendedDtlCds={extendedDtlCds}
        WitnessTempSave={WitnessTempSave}
        WitnessSave1={WitnessSave1}
        WitnessWorkEnd={WitnessWorkEnd}
        startWorkEndSignFlow={startWorkEndSignFlow}
        unsignedForWorkEnd={unsignedForWorkEnd}
        signFlowTabId={signFlowTabId}
        signFlowTrigger={signFlowTrigger}
        isWorkEndSignFlow={isWorkEndSignFlow}
        witnessSignFiles={witnessSignFiles}
        witnessSignUrls={witnessSignUrls}
      />
    </div>
  );
}
